"use client";
import React, { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  Check,
  KeyRound,
  ListChecks,
  LogIn,
  LogOut,
  Plug,
  Plus,
  Pencil,
  RefreshCw,
  Trash2,
} from "lucide-react";
import { useAuthedApi } from "../../api/useAuthedApi";
import { EmptyState } from "../Common/EmptyState";
import { PageHeader } from "../Common/PageHeader";
import { CardSkeletonList } from "../Common/CardSkeleton";
import { AddMcpWizard, type McpEditInitial } from "./AddMcpWizard";
import { ManageToolsDialog } from "./ManageToolsDialog";
import type { CustomMcpListing, McpScope } from "../../api/customMcpApi";

/** Manage user-added MCP connectors. A scope toggle switches between the
 *  company-wide surface (admin) and the caller's personal MCPs. Add opens the
 *  four-step wizard. Secrets are never shown — the backend redacts them. */
export function McpConnectorList({
  embedded = false,
  browseAction,
}: {
  embedded?: boolean;
  /** Optional extra action (e.g. "Browse marketplace") rendered in the header
   *  next to the scope toggle + Add. The unified Plugins page passes this so the
   *  marketplace catalog is one click away without a separate section. */
  browseAction?: React.ReactNode;
}) {
  const { customMcp } = useAuthedApi();
  const [scope, setScope] = useState<McpScope>("company");
  const [rows, setRows] = useState<CustomMcpListing[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [wizardOpen, setWizardOpen] = useState(false);
  const [editInitial, setEditInitial] = useState<McpEditInitial | null>(null);
  const [manageTools, setManageTools] = useState<{ name: string; display_name: string } | null>(
    null,
  );
  // Attach-token dialog for static connectors. For per-user installs each
  // end-user hits this to (re)paste their own token; for shared installs an
  // admin uses it to rotate the shared secret without re-adding the connector.
  const [attachToken, setAttachToken] = useState<{
    name: string;
    display_name: string;
    static_mode: "bearer" | "header" | "basic";
    static_header_name: string | null;
  } | null>(null);
  const [attachSecret, setAttachSecret] = useState("");

  const refresh = useCallback(async () => {
    try {
      setError(null);
      setRows(await customMcp.list(scope));
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to load connectors");
      setRows([]);
    }
  }, [customMcp, scope]);

  useEffect(() => {
    setRows(null);
    void refresh();
  }, [refresh]);

  // Keep connection status current without polling: the server recomputes
  // `connected` live on every list call, so a silent re-fetch when the tab
  // regains focus (or becomes visible) reflects tokens connected/expired/
  // revoked elsewhere. No spinner flash — we don't null `rows` here.
  useEffect(() => {
    const onFocus = () => void refresh();
    const onVisible = () => {
      if (document.visibilityState === "visible") void refresh();
    };
    // The OAuth callback (connector_oauth) postMessages the opener when the
    // popup finishes — the explicit, reliable signal to flip a connector to
    // "connected" without a manual reload. Focus/visibility alone can miss it:
    // after a cross-origin popup redirect the parent doesn't always fire focus.
    const onMessage = (e: MessageEvent) => {
      const t = (e.data && (e.data as { type?: string }).type) || "";
      if (t === "connector_connected" || t === "artie:connector_connected") {
        void refresh();
      }
    };
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("message", onMessage);
    return () => {
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("message", onMessage);
    };
  }, [refresh]);

  const handleRediscover = async (name: string) => {
    setBusy(name);
    setError(null);
    try {
      await customMcp.discover(scope, name);
      await refresh();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Discovery failed");
    } finally {
      setBusy(null);
    }
  };

  const handleConnect = async (name: string) => {
    setBusy(name);
    setError(null);
    try {
      const { authorize_url } = await customMcp.connect(name);
      window.open(authorize_url, "_blank", "noopener");
    } catch (e) {
      // 409 => the server isn't OAuth-discoverable; guide to a manual mode.
      const msg = e instanceof Error ? e.message : "Connect failed";
      setError(
        /409|discover|fallback/i.test(msg)
          ? "This server isn't auto-discoverable. Re-add it with a static token or client credentials."
          : msg,
      );
    } finally {
      setBusy(null);
    }
  };

  const handleDisconnect = async (name: string) => {
    if (!window.confirm(`Disconnect "${name}"? You'll need to sign in again to use it.`))
      return;
    setBusy(name);
    setError(null);
    try {
      await customMcp.disconnect(scope, name);
      await refresh();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Disconnect failed");
    } finally {
      setBusy(null);
    }
  };

  const handleAttachToken = async () => {
    if (!attachToken) return;
    const secret = attachSecret.trim();
    if (!secret) return;
    setBusy(attachToken.name);
    setError(null);
    try {
      await customMcp.attachSecret(attachToken.name, {
        mode: attachToken.static_mode,
        header_name:
          attachToken.static_mode === "header"
            ? attachToken.static_header_name || undefined
            : undefined,
        secret,
      });
      // Re-discover so the fresh secret is exercised immediately (populates
      // discovered_count on the row).
      await customMcp.discover(scope, attachToken.name);
      setAttachToken(null);
      setAttachSecret("");
      await refresh();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to save token");
    } finally {
      setBusy(null);
    }
  };

  const handleRemove = async (name: string) => {
    if (!window.confirm(`Remove "${name}"? This revokes any stored secret.`)) return;
    setBusy(name);
    setError(null);
    try {
      await customMcp.remove(scope, name);
      await refresh();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Remove failed");
    } finally {
      setBusy(null);
    }
  };

  // Default (medium) size, matching the action buttons in every other
  // page's PageHeader (Projects' "New project", Skills' "Marketplace") — this
  // row used to be all size="small", so it sat visibly shorter than the same
  // header slot everywhere else.
  const actions = (
    <Stack direction="row" spacing={1} alignItems="center">
      {browseAction}
      <Button
        variant={scope === "company" ? "contained" : "outlined"}
        onClick={() => setScope("company")}
      >
        Company
      </Button>
      <Button
        variant={scope === "user" ? "contained" : "outlined"}
        onClick={() => setScope("user")}
      >
        Personal
      </Button>
      <Button
        variant="contained"
        startIcon={<Plus size={16} />}
        onClick={() => {
          setEditInitial(null);
          setWizardOpen(true);
        }}
      >
        Add plugin
      </Button>
    </Stack>
  );

  return (
    <Box aria-label="mcp-connector-list">
      {/* Same PageHeader every workspace page uses, so title size/weight and
          the top-right actions row line up identically across pages — this
          used to roll its own smaller ad hoc title bar when embedded. */}
      <PageHeader
        title={embedded ? "Plugins & Connectors" : "Plugins"}
        subtitle={
          embedded
            ? undefined
            : "Add a plugin by its MCP server URL and choose which tools to expose. Secrets are stored encrypted and never shown again."
        }
        actions={actions}
      />

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {rows === null && <CardSkeletonList count={3} />}

      {rows !== null && rows.length === 0 && (
        <EmptyState
          icon={Plug}
          title="No plugins yet"
          description={
            scope === "company"
              ? "Add a company-wide plugin so everyone can use its tools."
              : "Add a personal plugin that only you can use."
          }
          action={
            <Button
              variant="contained"
              startIcon={<Plus size={16} />}
              onClick={() => { setEditInitial(null); setWizardOpen(true); }}
            >
              Add plugin
            </Button>
          }
        />
      )}

      {rows !== null && rows.length > 0 && (
        <Stack spacing={1.5}>
          {rows.map((c) => (
            <Card key={c.name} variant="outlined">
              <CardContent
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 2,
                }}
              >
                <Box sx={{ minWidth: 0 }}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Typography variant="subtitle2" noWrap>
                      {c.display_name || c.name}
                    </Typography>
                    <Chip
                      size="small"
                      label={
                        c.auth_mode === "static"
                          ? "static"
                          : c.auth_mode === "mcp_oauth"
                            ? "auto oauth"
                            : "oauth"
                      }
                    />
                    {(c.auth_mode === "mcp_oauth" || c.auth_mode === "user_oauth") &&
                      (c.connected ? (
                        <Chip
                          size="small"
                          color="success"
                          variant="outlined"
                          icon={<Check size={14} />}
                          label="connected"
                        />
                      ) : (
                        <Chip size="small" color="default" variant="outlined" label="not connected" />
                      ))}
                    {!c.enabled && <Chip size="small" color="warning" label="disabled" />}
                  </Stack>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    noWrap
                    sx={{ display: "block", mt: 0.5 }}
                  >
                    {c.mcp_url}
                  </Typography>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: "block", mt: 0.25 }}
                  >
                    {c.curated_tools.length} exposed / {c.discovered_count} discovered
                  </Typography>
                </Box>
                <Stack direction="row" spacing={0.5}>
                  {/* Connect/Disconnect for ANY per-user OAuth connector —
                      user_oauth (paste provider creds) and mcp_oauth (negotiated)
                      both need a per-user sign-in. Gating on mcp_oauth alone hid
                      the Connect button for user_oauth, leaving it stuck on
                      "not connected". */}
                  {(c.auth_mode === "mcp_oauth" ||
                    c.auth_mode === "user_oauth") &&
                    (c.connected ? (
                      <Tooltip title="Disconnect (sign out)">
                        <span>
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleDisconnect(c.name)}
                            disabled={busy === c.name}
                            aria-label={`disconnect-${c.name}`}
                          >
                            <LogOut size={16} />
                          </IconButton>
                        </span>
                      </Tooltip>
                    ) : (
                      <Tooltip title="Connect (sign in)">
                        <span>
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => handleConnect(c.name)}
                            disabled={busy === c.name}
                            aria-label={`connect-${c.name}`}
                          >
                            <LogIn size={16} />
                          </IconButton>
                        </span>
                      </Tooltip>
                    ))}
                  {/* Attach/rotate token for static connectors. Per-user
                      installs need each end-user to (re)paste their own
                      token here; shared installs let an admin rotate the
                      shared secret without re-adding the connector. */}
                  {c.auth_mode === "static" &&
                    c.static_mode &&
                    c.static_mode !== "custom_headers" && (
                      <Tooltip
                        title={c.connected ? "Rotate token" : "Attach your token"}
                      >
                        <span>
                          <IconButton
                            size="small"
                            color={c.connected ? "default" : "primary"}
                            onClick={() =>
                              setAttachToken({
                                name: c.name,
                                display_name: c.display_name || c.name,
                                static_mode: c.static_mode as
                                  | "bearer"
                                  | "header"
                                  | "basic",
                                static_header_name: c.static_header_name ?? null,
                              })
                            }
                            disabled={busy === c.name}
                            aria-label={`attach-token-${c.name}`}
                          >
                            <KeyRound size={16} />
                          </IconButton>
                        </span>
                      </Tooltip>
                    )}
                  <Tooltip title="Re-discover tools">
                    <span>
                      <IconButton
                        size="small"
                        onClick={() => handleRediscover(c.name)}
                        disabled={busy === c.name}
                        aria-label={`rediscover-${c.name}`}
                      >
                        <RefreshCw size={16} />
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Tooltip title="Manage tools">
                    <span>
                      <IconButton
                        size="small"
                        onClick={() =>
                          setManageTools({
                            name: c.name,
                            display_name: c.display_name || c.name,
                          })
                        }
                        disabled={busy === c.name}
                        aria-label={`manage-tools-${c.name}`}
                      >
                        <ListChecks size={16} />
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Tooltip title="Edit credentials">
                    <span>
                      <IconButton
                        size="small"
                        onClick={() => {
                          setEditInitial({
                            name: c.name,
                            display_name: c.display_name,
                            mcp_url: c.mcp_url,
                            auth_mode: c.auth_mode,
                            static_mode: c.static_mode,
                            static_header_name: c.static_header_name,
                          });
                          setWizardOpen(true);
                        }}
                        disabled={busy === c.name}
                        aria-label={`edit-${c.name}`}
                      >
                        <Pencil size={16} />
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Tooltip title="Remove">
                    <span>
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleRemove(c.name)}
                        disabled={busy === c.name}
                        aria-label={`remove-${c.name}`}
                      >
                        <Trash2 size={16} />
                      </IconButton>
                    </span>
                  </Tooltip>
                </Stack>
              </CardContent>
            </Card>
          ))}
        </Stack>
      )}

      <AddMcpWizard
        open={wizardOpen}
        scope={scope}
        editInitial={editInitial}
        onClose={() => {
          setWizardOpen(false);
          setEditInitial(null);
        }}
        onAdded={() => {
          setWizardOpen(false);
          setEditInitial(null);
          void refresh();
        }}
      />

      <Dialog
        open={attachToken !== null}
        onClose={() => {
          if (busy) return;
          setAttachToken(null);
          setAttachSecret("");
        }}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>
          Attach token — {attachToken?.display_name}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {attachToken?.static_mode === "header"
              ? `Sent as header ${attachToken?.static_header_name}: <token>.`
              : attachToken?.static_mode === "basic"
                ? "Sent as HTTP Basic <token>."
                : "Sent as Authorization: Bearer <token>."}{" "}
            Stored encrypted at rest. Existing token (if any) is replaced.
          </Typography>
          <TextField
            autoFocus
            fullWidth
            type="password"
            label="Your token"
            value={attachSecret}
            onChange={(e) => setAttachSecret(e.target.value)}
            autoComplete="off"
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setAttachToken(null);
              setAttachSecret("");
            }}
            disabled={busy !== null}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleAttachToken}
            disabled={busy !== null || !attachSecret.trim()}
          >
            Save token
          </Button>
        </DialogActions>
      </Dialog>

      <ManageToolsDialog
        open={manageTools !== null}
        scope={scope}
        name={manageTools?.name ?? null}
        displayName={manageTools?.display_name}
        onClose={() => setManageTools(null)}
        onSaved={() => {
          setManageTools(null);
          void refresh();
        }}
      />
    </Box>
  );
}
